import { Controller } from "stimulus"
import videojs from 'video.js'
import touchOverlay from 'videojs-overlay-buttons'

import 'video.js/dist/video-js.css'
import 'videojs-contrib-quality-levels'
import 'videojs-hls-quality-selector'
import 'videojs-thumbnail-sprite'
import 'videojs-overlay-buttons/dist/videojs-overlay-buttons.css'

// Telemetry: report player errors to the LMS sink (Phase 0). Inline copy of
// app/frontend/utils/reportPlayerError.js — the webpacker and Vite trees do not
// share source, so a small duplicate is acceptable here.
const MAX_ERROR_REPORTS_PER_SESSION = 5;
let errorReportsSent = 0;

// Cold-start self-heal tuning: cross-origin AES-HLS can stall on the first play
// (the same-origin AES key round-trip loses the race against CDN-HIT segments).
// Rather than a dead black frame, we re-set the source and resume a few times.
const RECOVERY_MAX_ATTEMPTS = 3;
const RECOVERY_BACKOFF_MS = 800;
const STALL_TIMEOUT_MS = 10000;

function reportLegacyPlayerError(player, el, extra = {}) {
  if (errorReportsSent >= MAX_ERROR_REPORTS_PER_SESSION) return;
  errorReportsSent += 1;

  const mediaError = player && player.error();
  const body = JSON.stringify({
    video_id: (el && el.dataset && el.dataset.videoId) || '',
    source_url: (player && player.currentSrc()) || '',
    error_code: mediaError && mediaError.code != null ? String(mediaError.code) : 'unknown',
    error_message: mediaError && mediaError.message ? mediaError.message : '',
    position_seconds: (player && player.currentTime()) || 0,
    page_path: window.location.pathname,
    ...extra,
  });

  if (navigator.sendBeacon) {
    navigator.sendBeacon('/api/player_errors', new Blob([body], { type: 'application/json' }));
    return;
  }

  fetch('/api/player_errors', {
    body,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    method: 'POST',
  });
}


export default class extends Controller {
  connect() {}
  initialize() {
    this.playerTarget = this.element.querySelector("video")
    if (!this.playerTarget) return;
    // Dispose any stale player still bound to this element id before re-initializing.
    // The watch partial hardcodes the constant id "video-js-player", which collides
    // if the page is ever restored (Turbolinks / show_v2) without a full reload.
    const stalePlayer = videojs.getPlayer(this.playerTarget.id);
    if (stalePlayer) { try { stalePlayer.dispose(); } catch { /* best-effort, non-fatal */ } }
    if (!videojs.getPlugin('touchOverlay')) { videojs.registerPlugin('touchOverlay', touchOverlay); }

    var videoPlayer = videojs(this.playerTarget, {
      controls: true,
      fluid: true,
      playbackRates: [0.5, 1, 1.5, 2],
      userActions: {
        doubleClick: false
      },
      controlBar: {
        pictureInPictureToggle: false
      }
    });

    // Keep the big play button visible: this is click-to-play (no autoplay), and a
    // visible control is the always-available recovery if a start/stall ever fails.
    videoPlayer.touchOverlay({
      play: {}
    });

    const controlBar = videoPlayer.getChild('ControlBar');
    // const buttonNextVideo = new NextVideoButton(videoPlayer);
    // const buttonTimestamp = new ButtonTimestamp(videoPlayer);
    // const buttonShowModule = new ShowModuleButton(videoPlayer);
    const buttonFastForward = new fastForward(videoPlayer);
    const buttonFastBackward = new fastBackward(videoPlayer);
    const buttonTheaterVideo = new theaterVideo(videoPlayer);

    // controlBar.progressControl.disable()
    controlBar.progressControl.enable()
    const jsTimestamp = document.querySelector('.js-url-timestamp');
    var isControlsEnable
    var isVideoTalks

    if(jsTimestamp){
      isControlsEnable = jsTimestamp.dataset.controls;
      isVideoTalks = jsTimestamp.dataset.isVideoTalks;
    }

    if(isControlsEnable == "true"){

      // controlBar.addChild(buttonNextVideo, {
      //   name: 'Next Video'
      // }, 1);

      // controlBar.addChild(buttonTimestamp, {
      //   text: 'Timestamp'
      // }, 1);

      controlBar.addChild(buttonFastForward, {
        text: 'Fast Forward'
      }, 2);

      controlBar.addChild(buttonFastBackward, {
        text: 'Fast Backward'
      }, 2);

      let cbTheater = controlBar.addChild(buttonTheaterVideo, {
        text: 'Theater Mode'
      });
      controlBar.el().insertBefore(cbTheater.el(), controlBar.fullscreenToggle.el());
    }

    // if (isVideoTalks == "false"){
    //   controlBar.addChild(buttonShowModule, {
    //     text: 'Lihat Module'
    //   }, 1);
    // }

    this.setCustomVideoPlayer(videoPlayer);
    this.videoPlayer = videoPlayer;
    this.initSelfHeal(videoPlayer);
    // this.setThumbnails(videoPlayer);

    if(document.querySelector('.js-url-timestamp')){
      this.setTimeUpdate(videoPlayer);
      this.timeStampClick(videoPlayer);
    }

    if (this.element.dataset.fullscreen == "true"){
      videoPlayer.enterFullWindow();
    }

    if (this.element.dataset.onhover == 'true'){
      videoPlayer.bigPlayButton.hide();
      videoPlayer.controlBar.hide();
      videoPlayer.muted(true);
      this.element.addEventListener('mouseenter', function() {
        videoPlayer.play();
      }.bind(videoPlayer));

      this.element.addEventListener('mouseleave', function() {
        videoPlayer.pause();
      }.bind(videoPlayer));
      videoPlayer.pause();
    }

    this.watchedBar();
  }

  disconnect() {
    this.clearStallWatchdog();
    if (this.videoPlayer) {
      try { this.videoPlayer.dispose(); } catch { /* best-effort, non-fatal */ }
      this.videoPlayer = null;
    }
  }

  // Self-healing playback. Nothing auto-starts (click-to-play), so recovery only
  // ever runs after the user has actually pressed play. On a stall (no progress
  // after a `waiting`) or a fatal error, re-set the source and resume from the last
  // position — the programmatic equivalent of the "reload the video" workaround —
  // capped at RECOVERY_MAX_ATTEMPTS, after which the play button is restored.
  initSelfHeal(player) {
    const sourceEl = this.playerTarget.querySelector('source');
    this.sourceUrl = sourceEl ? sourceEl.getAttribute('src') : null;
    this.sourceType = (sourceEl && sourceEl.getAttribute('type')) || 'application/x-mpegURL';
    this.recoverAttempts = 0;
    this._stallTimer = null;
    this._userWantsPlay = false;

    player.on('play', () => { this._userWantsPlay = true; });
    player.on('ended', () => { this._userWantsPlay = false; this.clearStallWatchdog(); });
    player.on('playing', () => this.clearStallWatchdog());
    player.on('timeupdate', () => this.clearStallWatchdog());
    player.on('waiting', () => this.armStallWatchdog());
    player.on('error', () => {
      reportLegacyPlayerError(player, this.element, { phase: 'error' });
      this.attemptRecovery();
    });
  }

  armStallWatchdog() {
    if (this._stallTimer || !this._userWantsPlay) return;
    if (!this.videoPlayer || this.videoPlayer.paused() || this.videoPlayer.ended()) return;

    this._stallTimer = setTimeout(() => {
      this._stallTimer = null;
      const player = this.videoPlayer;
      if (!player || player.paused() || player.ended()) return;
      this.attemptRecovery();
    }, STALL_TIMEOUT_MS);
  }

  clearStallWatchdog() {
    if (this._stallTimer) {
      clearTimeout(this._stallTimer);
      this._stallTimer = null;
    }
  }

  attemptRecovery() {
    const player = this.videoPlayer;
    if (!player || !this.sourceUrl) return;
    if (this.recoverAttempts >= RECOVERY_MAX_ATTEMPTS) return this.giveUp();

    this.recoverAttempts += 1;
    this.clearStallWatchdog();
    let resumeAt = 0;
    try { resumeAt = player.currentTime() || 0; } catch { resumeAt = 0; }
    const resumePlay = this._userWantsPlay;

    setTimeout(() => {
      const p = this.videoPlayer;
      if (!p) return;
      p.src({ src: this.sourceUrl, type: this.sourceType });
      p.load();
      p.ready(() => {
        try { if (resumeAt > 0) p.currentTime(resumeAt); } catch { /* best-effort, non-fatal */ }
        if (resumePlay) {
          const promise = p.play();
          if (promise && promise.catch) promise.catch(() => {});
        }
      });
    }, RECOVERY_BACKOFF_MS * this.recoverAttempts);
  }

  giveUp() {
    reportLegacyPlayerError(this.videoPlayer, this.element, { phase: 'gave_up' });
    if (this.videoPlayer && this.videoPlayer.bigPlayButton) {
      try { this.videoPlayer.bigPlayButton.show(); } catch { /* best-effort, non-fatal */ }
    }
  }

  setCustomVideoPlayer(videoPlayer){
    videoPlayer.hlsQualitySelector({ displayCurrentQuality: true})
  }

  // setThumbnails(videoPlayer){
    // if (this.element.dataset.thumbnails){
    //   this.thumbnails = JSON.parse(this.element.dataset.thumbnails)
    //   videoPlayer.thumbnailSprite({
    //     sprites: this.thumbnails,
    //   });
    // }
  // }

  timeStampClick(videoPlayer){
    let timeStamps = document.querySelectorAll(".js-timestamp");
    timeStamps.forEach(
      function(row){
        row.addEventListener('click',
          function(tm){
            let textTime = tm.currentTarget.querySelector(".font-semibold").innerText
            let curr_time = parseInt(window.Util.covertTime(textTime))
            this.player_.currentTime(curr_time);
            this.player_.pause();
          }.bind(videoPlayer)
        );
      }.bind(videoPlayer)
    )
  }

  setTimeUpdate(videoPlayer){
    videoPlayer.on('timeupdate', function() {
      const jsTimestamp = document.querySelector('.js-url-timestamp');
      const timestamp = this.currentTime().toFixed(0);
      const start_time = parseInt(this.currentTime().toFixed(0));
      const end_time = parseInt(start_time + 5);
      const url = jsTimestamp.dataset.url
      const time = jsTimestamp.dataset.time
      const max_timestamp = this.duration().toFixed(0);
      const isUpdateTimestamp = jsTimestamp.dataset.isUpdateTimestamp
      // const watchVideoId = jsTimestamp.dataset.watchVideoId
      const isTrue = (
        (max_timestamp != "NaN") &&
        (isUpdateTimestamp == "true") &&
        ((timestamp.charAt(timestamp.length - 1) == '0') ||
        timestamp.charAt(timestamp.length - 1) == '5') &&
        (parseInt(timestamp) > parseInt(time))
      )

      const timePercent = parseInt((timestamp/max_timestamp) * 100)
      if (timePercent == 1 || timePercent != this.newTimepercent){
        const EltimePercent = document.querySelector(`#stime-${timePercent - 1}`)
        if (EltimePercent){
          EltimePercent.remove()
          this.newTimepercent = timePercent
        }
      }

      if (isTrue){
        jsTimestamp.dataset.time = timestamp
        window.Ajax.get(url, {
          headers: [
            {
              key: 'timestamp',
              value: timestamp
            },
            {
              key: 'duration',
              value: max_timestamp
            },
            {
              key: 'starttime',
              value: start_time
            },
            {
              key: 'endtime',
              value: end_time
            },
            {
              key: 'Content-Type',
              value: 'application/json'
            }
          ],
          onSuccess: function(response){
            console.log(response)
          },
          onFail: function(response){
            console.log(response)
          }
        });
      }
    });
  }

  watchedBar(){
    const html = `<div class="vjs-load-progress" style="width: 100%;color: red;background-color: red;top: 6px;" id="time-s"></div>`
    const div = document.createElement("div")
    div.innerHTML = html
    const para = div.firstChild
    const x = document.querySelector(".vjs-load-progress")
    x.parentElement.insertBefore(para, x)

    const jsTimestamp = document.querySelector('.js-url-timestamp');
    // watch-times is absent on the admin detail page (only user-facing pages set it);
    // default to an empty array so the bar renders instead of throwing on JSON.parse(undefined).
    const watchTimes = JSON.parse((jsTimestamp && jsTimestamp.dataset.watchTimes) || '[]')
    for (let i = 0; i < 100; i++) {
      if (!watchTimes.includes(i)){
        const html = `<div id="stime-${i}" style="left: ${i}%;width: 1%;"></div>`;
        const div = document.createElement("div")
        div.innerHTML = html;
        const para = div.firstChild;
        document.querySelector("#time-s").appendChild(para)
      }
    }
  }
}

class NextVideoButton extends videojs.getComponent('Button') {
  constructor(player, options = {}) {
    super(player, options);
    this.addClass('fas')
    this.addClass('fa-angle-double-right');
  }

  handleClick(_e) {
    let nextVideoButton = document.querySelector(".js-next-video");
    nextVideoButton.click();
  }
}

// class ShowModuleButton extends videojs.getComponent('Button') {
//   constructor(player, options = {}) {
//     super(player, options);
//     this.addClass('text__video');
//     this.addClass('tooltiptext');
//     this.addClass('fas');
//     this.addClass('fa-book-open');
//   }

//   handleClick(_e) {
//     // let url = "/libraries"
//     let url = "https://taxbase.taxsam.co/library/modul"
//     // location.href = "/libraries"
//     window.open(url, '_blank');
//   }
// }

// class ButtonTimestamp extends videojs.getComponent('Button') {
//   constructor(player, options = {}) {
//     super(player, options);
//     this.addClass('flaticon2-rectangular');
//   }

//   handleClick(_e) {
//     console.log("timestamp")
//     const modalTimestamp = new ModalTimestamp(this.player_,
//       {
//         temporary: false,
//         description:'description',
//         label:"test",
//         closeable:true
//     });
//     this.player_.addChild(modalTimestamp);
//     modalTimestamp.on('modalopen', function() {
//       modalTimestamp.contentEl().innerHTML = document.querySelector("#modal-timestamp").innerHTML;
//     });
//     modalTimestamp.on('modalclose', function() {
//       this.player_.removeChild(modalTimestamp);
//     });
//     modalTimestamp.open();
//   }
// }

// class ModalTimestamp extends videojs.getComponent('ModalDialog'){
//   constructor(player, options = {}) {
//     super(player, options);
//   }
// }

class theaterVideo extends videojs.getComponent('Button') {
  constructor(player, options = {}) {
    super(player, options);
    this.addClass('fas');
    this.addClass('fa-arrows-alt-h');
  }

  handleClick(_e) {
    let videoBox = document.querySelector("#videoBox")
    if (videoBox){
      if (videoBox.classList.contains("col-12")){
        videoBox.classList.remove("col-12")
      }else {
        videoBox.classList.add("col-12")
      }
    }
  }
}

class fastForward extends videojs.getComponent('Button') {
  constructor(player, options = {}) {
    super(player, options);
    this.addClass('fas');
    this.addClass('fa-step-forward');
  }

  handleClick(_e) {
    this.player_.currentTime(this.player_.currentTime() + 10);
  }
}

class fastBackward extends videojs.getComponent('Button') {
  constructor(player, options = {}) {
    super(player, options);
    this.addClass('fas');
    this.addClass('fa-step-backward');
  }

  handleClick(_e) {
    this.player_.currentTime(this.player_.currentTime() - 10);
  }
}
